import * as THREE from "three";
import { PMREMGenerator, TextureLoader } from "three";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls";
import { TransformControls } from 'three/examples/jsm/controls/TransformControls';
import { CSS2DObject, CSS2DRenderer } from 'three/examples/jsm/renderers/CSS2DRenderer.js';
import { defaults, m2ft, ft2m } from "../../../../../Constants";
import React from "react";
import JSZip from "jszip";
import { SelectionHelper } from "three/examples/jsm/interactive/SelectionHelper";
import { RoomEnvironment } from "three/examples/jsm/environments/RoomEnvironment";
import { FBXLoader } from "three/examples/jsm/loaders/FBXLoader";
import {
    CheckModulesOverlay,
    DeleteObst,
    RemoveTreeFromCart,
    SetObstProperty,
    SetTreeProperty
} from "../Controllers/ObstructionControl";

import {
    RotateCamera,
    SetCameraAngle,
    SetCameraView,
    SetCameraZoom,
    SetCompassDeg,
    SetInitCamRotVal,
    SetMidpointView,
    SetTopView
} from "../Controllers/CameraControl";
import {
    DownloadGLTF,
    EmptyGroup,
    ExportGen,
    GetClickObj,
    GetDateVal,
    GetMousePos,
    GetParentTool,
    isIpadDevice,
    isMobile,
    isModuleMesh,
    SetMatEmissive,
    SetVisibleGroup
} from "../Controllers/Common";
import {
    ChangeMeshMaterial, disposeObject,
    patchOBBForTriangleIntersection,
    ResetWallMeshArr,
    SetWallMeshArr,
    SetWallMeshOBB
} from "../Controllers/MeshControl";
import {
    GetPlane,
    LoadModelJson,
    LoadPlane,
    LoadStreetView,
    LoadTreePerimeter,
    Obstruction_Tree_Initial_Setup,
    testMesh,
} from "../Controllers/Loader";
import {
    SelectAllTrees,
    SetTreeTransArr,
    SetupObstUI,
    SetupTreeGroup,
    TransChange,
    TransDown,
    TransUp
} from "../Controllers/TransControl";
import {
    CheckDragableObst,
    HideObject,
    PutDragObst,
    SetDragObst,
    SetObstVPos,
} from "../Controllers/ObstructionControl";
import { SetCloneVirObj, SetCopyVirObj, SetVirMeshPos } from "../Controllers/PushControls";
import {
    AddAutoAlignPoint,
    AddPointLine,
    CreateLineMesh,
    DrawAutoAlignLine,
    DrawLine,
    InitGuideLineGroup,
    InitLineGroup,
    ResetLineMesh,
    SetFirstPoint,
} from "../Controllers/LineControl";
import {CheckSunTimePlay, LoadSunGroup, SetSunGroupPos, SunInfo, SunVecArr} from "../Sun-Vector-Path/LoadSun";
import { DragMapCustom, fetchCustomMap, FetchMapImage, SetMapCustomSize, ViewAlertLatLon } from "../Controllers/MapControl";
import { ReCalcShade, RefreshShade, SetDisplayShadePoints, GetShadeCol } from '../Controllers/RefreshShade';
import { AddStep, SetStep } from "../Controllers/StepControl";
import {
    addModulesToAllRoofsMaxFill,
    addModulesToRoofAutoFill,
    DeleteModule,
    FlipDir,
    GetPosModuleDown,
    GetSelModules,
    InitModulePush, ModulePlacementStatusDeactivatedRoof,
    ModulePlacementStatusOk, ModulePlacementStatusRoofPitch, ModulePlacementStatusRoofShape,
    ModulePlacementStatusSetback,
    MoveModuleState,
    ReadyTransModules,
    ResetSelModuleArr,
    SetBuildingModule,
    SetClearFaceModule,
    SetModuleOriPos, SetModuleSize,
    SetSelModuleArr,
    SetVirModuleSize, SwitchModuleIrrMat,
    timeModuleDelay,
    TransModule,
    UpdateSelModuleArr
} from "../Controllers/ModuleControl";
import SelectionBox from "../Box-Select/SelectionBox";
import { ShowSelectRoof } from "../Controllers/RoofSelectControl";

import { CreatePlaneRoof, SetupFlatRoof } from "../Controllers/PlaneRoofControls";
import { SetTogglePathwayValue } from "../Controllers/PathwayControl";
import { ClearModuleEmissive, SelectBoxModule, SetBoxModulesPos } from "../Box-Select/ModuleBoxControl";
import moduleTextureForMeasureLayout from "../../../../../assets/img/textures/Measure-Layout-Module.jpg";


import { useViewerStore } from "../../../../../store/store";
import sleep from "../../../../../helpers/sleep";
import {getPathname, getPathnameIncludes} from "../../../../../helpers/getPathname";
import {siteCalculationState} from "../../../../../services/siteCalculationState";
import {QuestionableRoof, unsetQuestionableRoof } from "../../Modal/Modal-Content/QuestionableRoof";
import SpinAllModules from "../../Modal/Modal-Content/SpinAllModules";
import TreeCartForm from "../../Popout-Window/Tree-Cart-Popout/Tree-Cart/TreeCartForm";
import { OBB } from "three/examples/jsm/math/OBB";
import DiscardBuildingPopup from "../../Modal/Modal-Content/DiscardBuildingPopup";
import {
    clickButtonWithRef,
    isDesignOriginalSite,
    removeSelectedTree,
    resetLayers,
    setPathname,
    toggleLayer
} from "../../../../../helpers/storeHelpers";
import { AlertType, CalculationType, CustomImageProperty, EditorControlMode, HideObjectType, ImageryOptions, ModuleOrientation, ObstProperty, ReportType, ScreenShotType, SelectType, EditAction, TabName, TreeProperty } from "../../../../../@types/enums";
import {
    AddressInfo, BuildingAlt,
    BuildingDesignData, Coordinates,
    FaceItem, LayerOption, MonthlyIrradianceDataList, ObstructionMesh, RoofMesh, RoofUserData, StepInfo, TreeMesh, WallMesh, XYZCoordinates
} from "../../../../../@types/types";
import { ViewerAlert } from "../../../../../@types/store";
import { ReLabelTreeGroup, ToggleModuleMultiplierLabel, ToggleRoofIDTriangle, ToggleRoofTag, ToggleTreeLabel, ToggleTreeMultiplierLabel, UpdatePitchOverrideText } from "../Controllers/LabelControl";
import { BuildingAltitude, ObstructionGroup, TreeGroup, XZCoordinates } from "../../../../../@types/types";

const dateToday = new Date(), year = dateToday.getFullYear(), dateFirst = new Date("01/01/" + year); // , timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
const mobileDevice = isMobile();


let lastClickTime = 0;



interface SunVec {
    azimuth: number;
    altitude: number;
}

interface Box2D {
    s: { x: number; y: number } | null;
    e: { x: number; y: number } | null;
}

export class Viewer {

    // View/ Edit Modes
    isViewerInSplitMode = false;
    measureMode = false;
    viewMode: '2d' | '3d' = '3d';
    moveMode = false;
    autoAlignMode = false;
    autoScaleMode = false;
    autoRotate = false;
    drawMode = false;
    pushMode = false;
    isPushPullMode = false;
    createPlaneMode = false;
    createRoofMode = false;
    terrainView = true;
    mapView = true;
    mapTexture: THREE.Texture | null = null;
    map64Binary: string = '';

    // Variables - Arrays
    linePosArr: any[] = []; crownMapArr: any[] = []; meshArr: any[] = []; roofMeshArr: RoofMesh[] = [];
    edgeMeshArr: any[] = []; selTreeArr: any[] = []; wallMeshArr: WallMesh[] = []; obstMeshArr: ObstructionMesh[] = [];
    buildingArr: BuildingWise33PercentRuleInfo[] = []; nodeArr: any[] = []; terrainArr: any[] = []; moduleMeshArr: any[] = [];
    flatFrooEditLabel = []; pathwayMeshArr: any[] = []; selTypeMeshArr: any[] = []; roofs: any[] = [];
    roofLines: any[] = []; nodeRoofArr: any[] = [];

    // Variables - Irradiance Data
    irrCSVDateArr: any[] | null = null; // Hourly Irradiance Data
    irrCSVMainData: { utc: number; lat: number; lon: number; } | null = null;
    irradianceMonth: MonthlyIrradianceDataList = [];
    sunVecArr: SunVecArr | null = null; // Hourly Sun Position Data
    customTerrainData: number[][] = [];

    // Variables - Positions
    boxPos2D: Box2D = { s: null, e: null };
    upPos: { x: number, y: number } | null = null;
    downPos: { x: number, y: number } | null = null;
    posModuleDown: XYZCoordinates | null = null;
    posPlaneDown: XYZCoordinates | null = null;
    posMapDown: XZCoordinates | null = null;
    houseTransPos: XZCoordinates | null = null;

    // ThreeJS Environment
    scene: THREE.Scene;
    camera: THREE.OrthographicCamera; // | THREE.PerspectiveCamera;
    renderer: THREE.WebGLRenderer;
    cssLabelrenderer: CSS2DRenderer | any;
    neutralEnvironment: THREE.Texture;
    ambientLight = new THREE.AmbientLight(0xFFFFFF, 0.3);
    controls: OrbitControls;
    pmremGenerator: PMREMGenerator;
    selectionBox: SelectionBox | null = null;
    helper: SelectionHelper | null = null
    transform: TransformControls | any;

    // ThreeJS Groups & Meshes
    totalGroup: THREE.Group;
    sunGroup: THREE.Group;
    lineGroup: THREE.Group;
    modelGroup: THREE.Group & {totalShadeCount?: number};
    boundGroup: THREE.Group;
    roofGroup: THREE.Group;
    treeGroup: THREE.Group;
    obstGroup: THREE.Group;
    treeLabelGroup: THREE.Group;
    guideLineGroup: THREE.Group;
    roofTagGroup: THREE.Group;
    treeTagGroup: THREE.Group;
    roofIDTriangleGroup: THREE.Group;
    trialGroup: THREE.Group;
    testMesh: THREE.Mesh;
    mapCustom: (THREE.Mesh<THREE.BufferGeometry, THREE.MeshStandardMaterial> & { aspectRatio?: number }) | null = null;
    planeMesh: THREE.Mesh;
    lineMesh: THREE.Line | null = null;
    virObs:  ObstructionGroup | any= null;
    virTree: TreeGroup | any = null;
    virModule = new THREE.Group();
    treeTransMesh: TreeMesh | null = null;
    autoAlignLine1: THREE.Line | null = null;
    autoAlignLine2: THREE.Line | null = null;
    crownModel: any;
    
    // Edit Controls
    stepArr: StepInfo[] = [];
    stepNum = -1;
    stepMax = -1;

    // Variable - States
    cSize: { w: number, h: number } = { w: 0, h: 0 };
    tabInfo = 'Roof Properties';
    moduleDir = 'port';
    flagModulePathway = true;
    modulePlacementStatus = "";
    specialPathway = false
    FixedsetbackForAllEdges = false
    changeModuleTime = false;
    flagLineSnap = false;
    disLineSnap = 1.5;
    isObjectSelectedForPushPullTool = false;
    maxObstructionHeight = 50
    moveModuleState: MoveModuleState | null = null;
    roadNearPoint: XYZCoordinates | null = null;
    shiftBlackBordersValue = .1;
    autoScaleParams: any;
    roofRotY = 0;
    flagTransChange = false;
    buildingAlt: BuildingAltitude | null = null;
    mouseModuleStatus: 'down' | 'up' | 'drag' = 'up';
    loadingTerrain: boolean = false;

    // Selections
    selectType = SelectType.none;
    obstId: string | null = null;
    dragObst: any = null;
    preSelRoofId: string | null = null;
    objectSelectedForPushPullTool: any = null;
    selFlatRoof: RoofMesh | null = null;
    pushPullStartValue: number = 0;

    // Camera & Compass
    camPos3D: XYZCoordinates = { x: 0, y: 100, z: 80 };
    compassHeading = 0;
    compassDrag = false;
    cameraHeading = 0;
    camDis = 100;
    camPosY = 100;
    camRot = 0;

    // Site Data
    designFormat: 'JSON' | 'XML' | null = null;
    weatherStationName: string | null = null;
    roofJson: {
        opportunityId: string | null;
        fullAddress: string;
        structures: {
            roofs: any[];
            structureId: string;
            usedArea: number;
        }[];
    } | null = null;
    mapCoordinates: Coordinates;
    sunInfo: SunInfo = {
        date: GetDateVal(dateToday),
        lat: 0,
        lon: 0,
        zone: 0,
        time: dateToday.getHours() + dateToday.getMinutes() / 60,
        elevation: 0,
    };
    addressInfo: AddressInfo | null = null;
    wallHEdit: number = 0;

    // Timers & Counters
    buttonPressTimer: NodeJS.Timeout | null = null;
    roofModuleTime = false;
    resizeTimeout: NodeJS.Timeout | null = null;
    clickTimeout: NodeJS.Timeout | null = null;
    tapCount = 0;
    virFlatPoints: THREE.Group<THREE.Object3DEventMap>;
    virPlanePoints : THREE.Group<THREE.Object3DEventMap>;
    virFlatPlane : Plane;


    //Constructor
    constructor() {

        // Prepare Canvas Container
        const canvasContainer = document.getElementById("canvasContainer") as HTMLDivElement;
        if (!canvasContainer) {
            throw new Error("canvasContainer element not found");
        }
        const { clientWidth, clientHeight } = canvasContainer;
        this.cSize = { w: clientWidth, h: clientHeight };
        this.scene = new THREE.Scene();
        patchOBBForTriangleIntersection(OBB);

        // Reset Map Coordinate
        this.mapCoordinates = { latitude: 0, longitude: 0 }

        // Reset Viewer Modes
        this.drawMode = false;
        this.autoAlignMode = false;
        this.autoScaleParams = {};

        // Prepare Camera
        this.camera = new THREE.OrthographicCamera(this.cSize.w / -2, this.cSize.w / 2, this.cSize.h / 2, this.cSize.h / -2, 1, 1000);
        this.camera.zoom = 8;
        //make camera not cast shadow
        this.camera.castShadow = false;

        // 3D renderer properties
        this.renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true, preserveDrawingBuffer: true });
        this.renderer.shadowMap.enabled = true;
        this.renderer.shadowMap.type = THREE.PCFSoftShadowMap;
        this.renderer.setSize(this.cSize.w, this.cSize.h);
        this.renderer.domElement.style.position = 'absolute';
        this.renderer.domElement.style.top = '0';
        this.renderer.domElement.style.zIndex = '0';
        this.renderer.sortObjects = false;
        this.renderer.setPixelRatio(window.devicePixelRatio);
        this.renderer.toneMapping = THREE.LinearToneMapping;
        this.renderer.toneMappingExposure = 0.5;
        canvasContainer.appendChild(this.renderer.domElement);

        // CSS2D Renderer for Labels
        this.cssLabelrenderer = new CSS2DRenderer(); //new CSS2DRenderer({ preserveDrawingBuffer: true });
        this.cssLabelrenderer.setSize(this.cSize.w, this.cSize.h);
        this.cssLabelrenderer.domElement.style.position = 'absolute';
        this.cssLabelrenderer.domElement.style.top = '0';
        this.cssLabelrenderer.domElement.style.zIndex = '1';
        canvasContainer.appendChild(this.cssLabelrenderer.domElement);

        // Orbit/ Zoom Controls
        this.controls = new OrbitControls(this.camera, this.cssLabelrenderer.domElement);
        this.controls.maxDistance = 120;
        this.controls.minDistance = 5;
        this.controls.enableDamping = true;
        this.controls.dampingFactor = 0.09
        this.controls.minZoom = 0.5
        this.controls.mouseButtons = { LEFT: THREE.MOUSE.ROTATE, MIDDLE: THREE.MOUSE.PAN, RIGHT: null }
        this.controls.saveState()
        // Compass Controller
        this.controls.addEventListener('change', (e) => {
            //Dont change compass if pan
            const editorControlMode = useViewerStore.getState().editorControlMode;
            if (editorControlMode === 'pan') return;
            SetCompassDeg(this.camera, this);
        })

        //Scene Environment
        this.pmremGenerator = new PMREMGenerator(this.renderer);
        this.pmremGenerator.compileEquirectangularShader();
        this.neutralEnvironment = this.pmremGenerator.fromScene(new RoomEnvironment()).texture;
        this.scene.environment = this.neutralEnvironment;

        //Add Light
        this.ambientLight.intensity = 0.3
        this.scene.add(this.ambientLight);


        // Setup Mesh Group
        this.totalGroup = new THREE.Group();
        this.scene.add(this.totalGroup);
        this.sunGroup = new THREE.Group();
        this.sunGroup.visible = false;
        this.lineGroup = new THREE.Group();
        this.modelGroup = new THREE.Group();
        this.boundGroup = new THREE.Group();
        this.roofGroup = new THREE.Group();
        this.treeGroup = new THREE.Group();
        this.obstGroup = new THREE.Group();
        this.treeLabelGroup = new THREE.Group();
        this.guideLineGroup = new THREE.Group();
        this.roofTagGroup = new THREE.Group();
        this.trialGroup = new THREE.Group();
        this.roofIDTriangleGroup = new THREE.Group();
        this.treeTagGroup = new THREE.Group();

        // Setup Mesh Group Names and Visibility
        this.roofGroup.name = 'roofGroup';
        this.treeGroup.name = 'treeGroup';
        this.obstGroup.name = 'obstGroup';
        this.roofTagGroup.name = 'roofTagGroup';
        this.roofTagGroup.visible = false;
        this.roofIDTriangleGroup.name = 'roofIDTriangleGroup';
        this.roofIDTriangleGroup.visible = false;
        this.treeTagGroup.name = 'treeTagGroup'
        this.treeTagGroup.visible = false
        this.treeLabelGroup.name = 'treeLabelGroup';
        this.treeLabelGroup.visible = true
        this.testMesh = testMesh.clone();
        this.testMesh.visible = false;

        // Join all groups
        this.roofGroup.add(this.obstGroup);
        this.modelGroup.add(this.roofGroup, this.treeGroup, this.obstGroup, this.treeLabelGroup);
        this.totalGroup.add(this.sunGroup, this.lineGroup, this.guideLineGroup, this.modelGroup, this.boundGroup,
            this.roofTagGroup, this.treeTagGroup, this.roofIDTriangleGroup, this.trialGroup, this.testMesh);

        // Transform Controls
        this.transform = new TransformControls(this.camera, this.cssLabelrenderer.domElement);
        this.transform.showY = false;
        this.transform.setMode('translate');
        this.transform.size = 0.6;
        this.scene.add(this.transform);
        this.transform.addEventListener('change', (e: any) => TransChange(e, this));
        this.transform.addEventListener('mouseDown', (e: any) => TransDown(e, this));
        this.transform.addEventListener('mouseUp', (e: any) => TransUp(e, this));

        // Render Virtual Meshes - Floor Plane, Sun, Obstruction, Tree, Guide Line, Line Mesh
        LoadPlane(this); // Hidden Floor Plane Mesh
        Obstruction_Tree_Initial_Setup(this); // Default Obstruction and Tree Group Setup
        InitGuideLineGroup(this);
        CreateLineMesh(this);
        LoadSunGroup(this.sunGroup); // Load Sun Sphere
        LoadTreePerimeter(this); // Load Tree Perimeter Mesh

        // Hidden  Plane Mesh for Transformation
        this.planeMesh = GetPlane({ x: 1000, y: 1000 }, true);
        this.planeMesh.position.y = -0.1;
        this.planeMesh.name = 'plane';
        this.totalGroup.add(this.planeMesh);


        // Render the scene and start the animation loop
        this.animate();

        let isTouchEvent = false;

        // Add Mouse / Touch Event Listeners
        canvasContainer.addEventListener('mousedown', (event) => {
            if (!isTouchEvent) {
                this.onMouseDown(event);
            }
        });

        canvasContainer.addEventListener('mouseup', (event) => {
            if (!isTouchEvent) {
                this.onMouseUp(event);
            }
        });

        canvasContainer.addEventListener('touchstart', (event) => {
            isTouchEvent = true;
            this.onMouseDown(event);
        });

        canvasContainer.addEventListener('touchend', (event) => {
            isTouchEvent = true;
            this.onMouseUp(event);
            isTouchEvent = false; // Reset the flag after touch events are handled
        });

        canvasContainer.addEventListener('pointermove', this.onMouseMove); //Enough to handle all pointer and touch move events
    }


    viewerAlert = (
        {
            show = true,
            title = "",
            message = "",
            messageType = AlertType.info,
            isModal = false,
            onClose = () => { },
            isCloseAfterDisplay = false,
            autoCloseSeconds = 4000
        }: ViewerAlert
    ) => {
        useViewerStore.setState({
            viewerAlert: {
                show: show,
                title: title,
                message: message,
                messageType: messageType,
                isModal: isModal,
                onClose,
                isCloseAfterDisplay,
                autoCloseSeconds
            }
        })
    }

    removeViewerAlert = () => {
        useViewerStore.setState({
            viewerAlert: {
                show: false,
                isModal: false
            }
        })
    }

    animate() {
        requestAnimationFrame(this.animate.bind(this));
        if (!this.camera || !this.renderer) return;
        this.renderer.render(this.scene, this.camera);

        this.cssLabelrenderer.render(this.scene, this.camera);
        this.controls.update();
        if (this.autoRotate) RotateCamera(this);
        if (this.selectType === 'sun') CheckSunTimePlay(this);
        const conTarget = this.controls.target;

        this.camera.lookAt(conTarget.x, conTarget.y, conTarget.z);
    }


    // CAMERA CONTROLS
    setCameraView = (viewMode: '2d' | '3d', home?: boolean) => SetCameraView(this, viewMode, home);
    setCameraAngle = (rot: number) => SetCameraAngle(this, rot)
    setTopView = (val: boolean) => SetTopView(this, val);
    alignToRoof = (roofId: string) => ShowSelectRoof(this, roofId);
    setCameraZoom = (dir: number) => SetCameraZoom(this, dir);
    moveCameraToSiteMidpointView = () => SetMidpointView(this);
    disableControls = () => this.controls.enableRotate = false;
    enableControls = () => this.controls.enableRotate = true;

    setLineSnapDis = (value: number) => this.disLineSnap = value;
    setBuildingModule = (value: boolean) => SetBuildingModule(this, value);
    moveModuleDistance = (dir: string, inch: number) => UpdateSelModuleArr(this, 'moveInch', { dirStr: dir, inch });
    loadStreetView = () => LoadStreetView(this);
    setClearFaceModule = (roofMesh: RoofMesh) => SetClearFaceModule(this, roofMesh);
    setPushPullMode = (mode: boolean) => useViewerStore.setState({ isPushPullMode: mode });
    unsetQuestionable = (roof: RoofMesh) => unsetQuestionableRoof(roof);

    setupSelectionBoxHelper = (boxSelectType: SelectType) => {
        this.controls.enableRotate = true;
        document.body.style.cursor = 'crosshair';
        useViewerStore.setState({ isBoxSelectMode: true })
        this.disableControls()
        this.selectionBox = new SelectionBox(this.camera, this.scene);
        this.helper = new SelectionHelper(this.cssLabelrenderer, 'selectBox'); // cssLabelrenderer
        this.roofs[0].faces.forEach((face: any) => SetBoxModulesPos(this, face.roofFaceId));
        this.selectType = boxSelectType;
    }

    removeSelectionBoxHelper = () => {
        if (this.helper === null) return
        document.body.style.cursor = 'default'
        this.helper?.dispose();
        this.enableControls()
        useViewerStore.setState({ isBoxSelectMode: false })
        this.helper = null;
        this.selectionBox = null;
        this.boxPos2D = { s: null, e: null };
    }

    setMenu = (tabName: TabName) => {
        const { isBuildingChange } = useViewerStore.getState();
        if (tabName !== TabName.Building && isBuildingChange) {
            this.viewerAlert({
                show: true,
                title: "Discard Changes",
                message: <DiscardBuildingPopup
                    onConfirm={() => {
                        useViewerStore.setState({ isBuildingChange: false });
                        this.setMenu(tabName);
                    }} />,
                messageType: AlertType.info,
                isModal: true
            });
            return;
        } 

        useViewerStore.setState({ isRightSidebarOpen: tabName !== TabName.PackingSlip });
        
        switch (tabName) {
            case TabName.Roof:
            case TabName.Building:
            case TabName.Obstruction:
            case TabName.Tree:
            case TabName.Modules:
            case TabName.SolarAccess:
            case TabName.Irradiance:
            case TabName.WhatIf:
            case TabName.SurfaceDraw:
            case TabName.CustomImage: setPathname(`/configure/${tabName}`); break;
            case TabName.EPC: setPathname('/configure/price-calculation'); break;
            case TabName.PackingSlip: setPathname('/configure/packing-slip'); break;
            default: break;
        }
    }

    onMouseDown = (e: MouseEvent | TouchEvent) => {
        e.preventDefault()
        if (mobileDevice) {
            const meshType: any = GetClickObj(e, [...this.meshArr], this.camera, this.cSize);
            if (getPathnameIncludes('price-calculation') && meshType?.object?.meshType === "tree") {
                this.buttonPressTimer = setTimeout(() => useViewerStore.setState({ isTapandHold: true }), 500);
            }

            const interInfo: any = GetClickObj(e, [...this.pathwayMeshArr], this.camera, this.cSize);
            if (interInfo && interInfo.object.meshType === 'pathway') {
                const currentTime = new Date().getTime();
                const clickInterval = currentTime - lastClickTime;

                if (clickInterval < 350 && clickInterval > 0) {
                    this.tapCount++;
                    if (this.clickTimeout) clearTimeout(this.clickTimeout);

                    if (this.tapCount === 2) {
                        SetTogglePathwayValue(this, interInfo.object.pathwayId);
                        this.tapCount = 0;
                    } else if (this.tapCount > 2) {
                        this.tapCount = 0;
                    }
                } else {
                    this.tapCount = 1;
                }

                lastClickTime = currentTime;

                this.clickTimeout = setTimeout(() => this.tapCount = 0, 350);
            }
        }
        this.upPos = null;
        if (this.downPos) return;
        const { posX, posY } = GetMousePos(e);
        this.downPos = { x: posX, y: posY };

        const isPushPullMode = useViewerStore.getState().isPushPullMode

        if (isPushPullMode) {
            const interInfo: any = GetClickObj(e, [...this.meshArr], this.camera, this.cSize);
            if (interInfo && interInfo.object && interInfo.object.isMesh && interInfo.object.meshType === 'obst') {

                document.body.style.cursor = 'grabbing'
                this.objectSelectedForPushPullTool = interInfo.object
                this.isObjectSelectedForPushPullTool = true
                if (!getPathnameIncludes('obstruction')) {
                    useViewerStore.setState({ pathname: '/configure/obstruction' })
                }

                this.pushPullStartValue = interInfo.object.height;
                setTimeout(() => {
                    const newObstId = interInfo.object.obstId
                    SetupObstUI(this, newObstId);
                }, 100)


            } else if (interInfo && interInfo.object && interInfo.object.isMesh && interInfo.object.meshType === 'tree') {

                document.body.style.cursor = 'grabbing'
                this.objectSelectedForPushPullTool = interInfo.object
                this.isObjectSelectedForPushPullTool = true


                if (!getPathnameIncludes('tree')) {
                    useViewerStore.setState({ pathname: '/configure/tree' })
                }


                setTimeout(() => {
                    const newObstId = interInfo.object.obstId
                    SetupTreeGroup(this, newObstId, true);
                }, 100)
                this.pushPullStartValue = interInfo.object.totalH || interInfo.object.parent?.totalH || this.selTreeArr[0]?.totalH; // capture original height
            }
            return
        }


        const isBoxSelectMode = useViewerStore.getState().isBoxSelectMode

        if (isBoxSelectMode && this.selectionBox != undefined) {
            this.selectionBox?.startPoint.set(
                (posX / this.cSize.w) * 2 - 1,
                -(posY / this.cSize.h) * 2 + 1,
                0.5);

            this.boxPos2D.s = { x: posX, y: posY };
            // const pointerDownSelectedObject = useViewerStore.getState().pointerDownSelectedObject;
            // const selectedModule = GetSelModules(this.moduleMeshArr);

            // if (this.selectType === 'module') SetBoxModulesPos(this, selectedModule[0]);
            SetMatEmissive(this.selectionBox.collection, this.selectType, 'collection');
            return;
        }

        if (GetParentTool(e)) return;

        if (this.viewMode === '3d') {
            this.controls.maxPolarAngle = Math.PI / 2 - 0.1;
            if (this.selectType === 'obst' && this.obstId && this.moveMode) {
                this.dragObst = CheckDragableObst(e, this);
                if (this.dragObst && this.dragObst.obstId === this.obstId) this.controls.enabled = false;
            } else if (this.measureMode) {
                const interInfo: any = GetClickObj(e, [...this.roofMeshArr, ...this.wallMeshArr, this.planeMesh], this.camera, this.cSize);
                this.drawMode = true;
                AddPointLine(this, interInfo.point, interInfo.object.roofFaceId, interInfo);
            } else if (this.selectType === 'module') {
                SetObstVPos(this.obstGroup);
                if (this.moveMode) {
                    const interInfo = GetClickObj(e, [...this.roofMeshArr], this.camera, this.cSize);
                    this.posModuleDown = GetPosModuleDown(interInfo, this.moduleMeshArr);
                    this.moveModuleState = ReadyTransModules(this);
                }
                this.mouseModuleStatus = 'down';
            }
        } else {
            this.controls.maxPolarAngle = 0.01;
            const interInfo = GetClickObj(e, [this.planeMesh], this.camera, this.cSize);
            const manualZone = useViewerStore.getState().manualZone;
            if (this.measureMode || this.createRoofMode || manualZone) {
                this.drawMode = true;
                AddPointLine(this, interInfo.point, null, interInfo);
            }
        }
        if (this.selectType === 'mapCustom') {
            if (!this.mapCustom) return;
            if (this.moveMode) {
                const flagDownMap = GetClickObj(e, [this.mapCustom], this.camera, this.cSize);
                if (!flagDownMap) return;
                const interPlane = GetClickObj(e, [this.planeMesh], this.camera, this.cSize);
                this.posPlaneDown = interPlane.point;
                const { x, z } = this.mapCustom.position;
                const curPosX = x, curPosZ = z;
                this.posMapDown = { x: curPosX ?? 0, z: curPosZ ?? 0 };
            } else if (this.autoAlignMode) {
                const interPlane = GetClickObj(e, [this.mapCustom], this.camera, this.cSize);
                this.drawMode = true;
                AddAutoAlignPoint(this, interPlane.point);
            }
        }
    }

    onMouseMove = (e: MouseEvent | TouchEvent) => {
        e.preventDefault()

        const isPushPullMode = useViewerStore.getState().isPushPullMode
        const { posX, posY } = GetMousePos(e);

        if (e instanceof MouseEvent && isPushPullMode && this.isObjectSelectedForPushPullTool) {
            this.controls.enabled = false;
            document.body.style.cursor = 'grabbing';

            if (this.objectSelectedForPushPullTool.meshType === 'obst') {
                const height = this.objectSelectedForPushPullTool.height;
                const maxHeightMeters = this.maxObstructionHeight * ft2m;

                if (e.movementY > 0) {
                    //Decrease height
                    this.setObstHeight(height - 0.1)
                } else if (e.movementY < 0 && height <= maxHeightMeters) {
                    //Increase height

                    //just make height 6.096
                    if (height + 0.1 > maxHeightMeters) {
                        this.setObstHeight(maxHeightMeters)
                        return
                    }
                    this.setObstHeight(height + 0.1)
                }
            } else {
                let heightValue: any = this.objectSelectedForPushPullTool.totalH || this.objectSelectedForPushPullTool.parent?.totalH || this.selTreeArr[0]?.totalH;
                if (Array.isArray(heightValue)) heightValue = heightValue[0];
                let height = Number(heightValue);
                this.setTreeProperty(TreeProperty.height, Math.max(0.1, height - 0.1))
                
                if (e.movementY > 0) {
                    //Decrease height
                    this.setTreeProperty(TreeProperty.height, height - 0.1)
                } else if (e.movementY < 0 && height <= 30.48) {
                    //Increase height

                    //just make height 6.096
                    // if (height + 0.1 > 30.48) {
                    //     this.setTreeProperty(TreeProperty.height, 30.48)
                    //     return
                    // }
                    // this.setTreeProperty(TreeProperty.height, height + 0.1)
                    this.setTreeProperty(TreeProperty.height, Math.min(30.48, height + 0.1))
                }

            }
        }

        const isBoxSelectMode = useViewerStore.getState().isBoxSelectMode;
        if (isBoxSelectMode) {
            if (!this.helper?.isDown || !this.selectionBox) return;
            // if (!this.boxPos2D.s){
                // if (this.selectType === 'module'){
                //     const raycaster = new THREE.Raycaster();
                //     let mouse = new THREE.Vector2();
                //     raycaster.setFromCamera(mouse, this.camera);
                //     const intersects = raycaster.intersectObjects(this.roofMeshArr, false);
                //     const roofFaceId = intersects.find(i => i.object.roofFaceId)?.object.roofFaceId;
                //     if (roofFaceId) {
                //         SetBoxModulesPos(this, roofFaceId);
                //         this.selectionBox.startPoint.set(
                //             (posX / this.cSize.w) * 2 - 1,
                //             -(posY / this.cSize.h) * 2 + 1,
                //             0.5);

                //         this.boxPos2D.s = {x: posX, y: posY};
                //     }
                // } else {
            //         this.selectionBox.startPoint.set(
            //             (posX / this.cSize.w) * 2 - 1,
            //             -(posY / this.cSize.h) * 2 + 1,
            //             0.5);

            //         this.boxPos2D.s = {x: posX, y: posY};
            //     }
            // } else {
            SetMatEmissive(this.selectionBox.collection, this.selectType, 'collection');

            this.selectionBox.endPoint.set(
                (posX / this.cSize.w) * 2 - 1,
                -(posY / this.cSize.h) * 2 + 1,
                0.5);

            this.boxPos2D.e = { x: posX, y: posY };
            const allSelected = this.selectionBox.select(this.selectionBox.startPoint, this.selectionBox.endPoint);

            //select meshType module
            const moduleSelected = this.selectionBox.select(this.selectionBox.startPoint, this.selectionBox.endPoint).filter(mesh => mesh.name === 'moduleOutline')
            if (!moduleSelected.length) return;
            // if(!moduleSelected){
            //     moduleSelected = GetClickObj(e, this.moduleMeshArr.filter(module => {
            //         return module.pathwayOver !== true
            //     }), this.camera, this.cSize);
            // }
            const firstModule = moduleSelected[0].parent
            if (this.selectType === 'module' && firstModule) {
                const allModuleSelected = this.moduleMeshArr.filter(mesh => mesh.select);
                if (allModuleSelected.length > 0 && allModuleSelected[0].roofId !== firstModule.roofId) return;
                SelectBoxModule(this, firstModule);
            }
            SetMatEmissive(allSelected, this.selectType, 'allSelected', 'move');
            // }
        }


        if (this.selectType === 'mapCustom' && this.moveMode && this.posMapDown && this.posPlaneDown) {
            const interPlane = GetClickObj(e, [this.planeMesh], this.camera, this.cSize);
            DragMapCustom(this, interPlane.point);
        }

        if (GetParentTool(e)) return;
        if (this.dragObst) {
            SetDragObst(e, this);
            return;
        }
        if (this.pushMode) {
            SetVirMeshPos(this, e);
            return;
        }
        if (this.selectType === 'module') {
            if (this.posModuleDown) TransModule(e, this, this.moveModuleState);
            if (this.mouseModuleStatus === 'down') {
                this.mouseModuleStatus = 'drag';
            }
        }

        if (this.measureMode && this.linePosArr.length < 2) {
            const meshArr = this.viewMode === '2d' ? [this.planeMesh] : [this.mapCustom, ...this.meshArr];
            const interInfo = GetClickObj(e, meshArr, this.camera, this.cSize);
            if (interInfo) SetFirstPoint(this, interInfo.point);
        }

        if (this.drawMode) {
            if (this.autoAlignMode) {
                const interPlane = GetClickObj(e, [this.mapCustom!], this.camera, this.cSize);

                if (interPlane) DrawAutoAlignLine(this, interPlane.point);
            } else {
                const meshArr = this.viewMode === '2d' ? [this.planeMesh] : [this.mapCustom, ...this.meshArr];
                const interInfo = GetClickObj(e, meshArr, this.camera, this.cSize);
                if (interInfo) DrawLine(this, interInfo.point); //If statement to prevent console being dumped with undefined errors
            }
        }

        CheckModulesOverlay(this);
    }

    onMouseUp = (e: MouseEvent | TouchEvent) => {
        e.preventDefault()
        if ((mobileDevice || isIpadDevice()) && this.buttonPressTimer) {
            clearTimeout(this.buttonPressTimer);
        }
        const { isTapandHold, isPushPullMode, pathname, layerVisibilityState, isHideAllGhostObstructions, isHideAllGhostTrees } = useViewerStore.getState();

        if (isTapandHold) useViewerStore.setState({ isTabandHoldUp: true })
        const { posX, posY } = GetMousePos(e);
        var dragDis = 0;
        if (this.downPos) {
            dragDis = Math.abs(posX - this.downPos.x) + Math.abs(posY - this.downPos.y);
        }
        this.downPos = null;
        if (this.upPos) return;
        this.upPos = { x: posX, y: posY };

        if (isPushPullMode) {
            if (isPushPullMode && this.isObjectSelectedForPushPullTool) {
                this.controls.enabled = true
                document.body.style.cursor = 'grab'
                this.isObjectSelectedForPushPullTool = false
                const endValue = this.objectSelectedForPushPullTool.meshType === 'obst' ? this.objectSelectedForPushPullTool.height : this.objectSelectedForPushPullTool.totalH || this.selTreeArr[0]?.totalH;
                if (Math.abs(endValue - this.pushPullStartValue) > 0.001) {
                    const stepKey = this.objectSelectedForPushPullTool.meshType === 'obst' ? EditAction.ObstHeight : EditAction.TreeHeight;
                    const stepObj = this.objectSelectedForPushPullTool.meshType === 'obst' ? this.objectSelectedForPushPullTool.obstId : [this.objectSelectedForPushPullTool.obstId];
                    this.addStep(stepKey, stepObj, [this.pushPullStartValue], [endValue]);
                }
                
                if (Math.abs(endValue - this.pushPullStartValue) > 0.001) {
                    const meshType = this.objectSelectedForPushPullTool.meshType;
                    if (meshType === 'obst') {
                        const object = this.objectSelectedForPushPullTool.obstId;
                        this.addStep(EditAction.ObstHeight, object, [this.pushPullStartValue], [endValue]);
                    } else {
                        const object = this.objectSelectedForPushPullTool.children.map((child: any) => child.obstId)
                        this.addStep(EditAction.TreeHeight, object, this.pushPullStartValue, endValue);
                    }
                }
            }
            return;
        }
        // ON RIGHT CLICK
        if ((e instanceof MouseEvent && e.button === 2) || isTapandHold) {

            const isTreeLayerVisible = !pathname.includes('modules') && layerVisibilityState.tree;
            let arrayForRaycast: any[] = []

            if (isTreeLayerVisible) {
                arrayForRaycast = this.meshArr
            } else {
                //traverse and remove meshType tree
                //also remove obst.delStatus === true
                arrayForRaycast = this.meshArr.filter(mesh => {
                    return mesh.meshType !== 'tree'
                })
            }


            //Filter deactivated roof pathways
            arrayForRaycast.forEach((mesh) => {
                if (mesh.meshType === 'pathway' && mesh.visible === false) {
                    arrayForRaycast = arrayForRaycast.filter(m => m !== mesh)
                }
            })


            if (isHideAllGhostObstructions) {
                arrayForRaycast = arrayForRaycast.filter(mesh => {
                    return mesh.meshType !== 'obst' || mesh.delStatus !== true
                })
            }

            if (isHideAllGhostTrees) {
                arrayForRaycast = arrayForRaycast.filter(mesh =>
                    mesh.meshType !== 'tree' || (!mesh.parent.delStatus && mesh.visible && mesh.parent.visible))
            }

            arrayForRaycast.forEach((mesh) => {
                if (mesh.name.includes("wall")) {
                    arrayForRaycast = arrayForRaycast.filter(m => m !== mesh)
                }
            })

            const clickObj: any = GetClickObj(e, arrayForRaycast, this.camera, this.cSize),
                meshType = clickObj?.object?.meshType;

            var pointerDownSelectedObject = null, isPointerOnObject = false, pointerDownSelectedObjectType = null
            if (meshType === "obst" || meshType === "tree" || meshType === "module" || meshType === "roof" || meshType === "pathway") {
                if (this.pushMode) this.setPushMode(false);
                if (this.moveMode) this.setMoveMode(false);

                pointerDownSelectedObject = clickObj.object;
                isPointerOnObject = true;
                pointerDownSelectedObjectType = meshType
                this.selectType = meshType;
                if (meshType === "obst") {
                    this.tabInfo = 'Obstruction Properties'
                } else if (meshType === "roof") {
                    this.preSelRoofId = clickObj.object.roofFaceId;
                    const modeCount = clickObj.object.userData.moduleCount || { port: 0, land: 0, both: 0 };
                    this.selectRightClickRoof();
                    useViewerStore.setState({
                        clickedRoofAutofillModuleCountPortrait: modeCount.port,
                        clickedRoofAutofillModuleCountLandscape: modeCount.land,
                        clickedRoofAutofillModuleCountBoth: modeCount.both
                    });
                    this.tabInfo = 'Roof Properties'
                } else if (meshType === "tree" && isTreeLayerVisible) {
                    this.tabInfo = 'Tree Properties'
                } else if (meshType === "module") {
                    this.mouseModuleStatus = 'down'
                } else if (meshType === "pathway") {
                    this.mouseModuleStatus = 'down'
                }
            }
            useViewerStore.setState({
                pointerDownSelectedObject, isPointerOnObject, pointerDownSelectedObjectType
            })
            // return;
        }

        const isBoxSelectMode = useViewerStore.getState().isBoxSelectMode

        if (isBoxSelectMode) {
            this.selectionBox?.endPoint.set(
                (posX / this.cSize.w) * 2 - 1,
                -(posY / this.cSize.h) * 2 + 1,
                0.5);

            const allSelected = this.selectionBox?.select(this.selectionBox.startPoint, this.selectionBox.endPoint);

            if (allSelected?.length) {

                SetMatEmissive(allSelected, this.selectType, 'allSelected', 'up');

                if (this.selectType === "tree") {
                    for (let i = 0; i < allSelected.length; i++) {
                        if (allSelected[i].isMesh && allSelected[i].meshType === 'tree') {
                            // this.selectionBox.collection[ i ].material.emissive.set( 0x000000 );
                            SetupTreeGroup(this, allSelected[i].obstId, true)
                        }
                    }
                }
            }

            if (this.selectType === 'module') ClearModuleEmissive(this.moduleMeshArr);
            this.removeSelectionBoxHelper()

        }

        if (GetParentTool(e)) {
            return;
        }
        if (this.pushMode) {
            // update module placement validation

            const modulePlacementStatus = this.modulePlacementStatus

            if (modulePlacementStatus === ModulePlacementStatusRoofShape) {
                this.viewerAlert({
                    show: true,
                    title: "Warning",
                    message: "Modules cannot be placed on Accessory Buildings",
                    messageType: AlertType.warning,
                    isModal: false,
                    isCloseAfterDisplay: true,
                })
            }

            //DEBUG: Log reason for module placement / not being placed
            console.log(this.modulePlacementStatus, 'self.modulePlacementStatus')

            if (modulePlacementStatus === ModulePlacementStatusDeactivatedRoof) {
                this.viewerAlert({
                    show: true,
                    title: "Warning",
                    message: "Cannot place modules on deactivated roof , please activate the roof to place modules",
                    messageType: AlertType.warning,
                    isModal: false,
                    isCloseAfterDisplay: true,
                })
            }
            if (modulePlacementStatus === ModulePlacementStatusRoofPitch) {
                this.viewerAlert({
                    show: true,
                    title: "Roof Pitch Error",
                    message:
                        `
                        Cannot place regular modules on this roof because the roof pitch is below 3 degree or above 50 degree.
                        
                        Ballasted Racking recommended for flat roofs
                        `,
                    messageType: AlertType.error,
                    isModal: false,
                    isCloseAfterDisplay: true,
                })
            }

            if (modulePlacementStatus === ModulePlacementStatusSetback) {
                this.viewerAlert({
                    show: true,
                    title: "Capability Limitation",
                    message:
                    <>
                        <p>You do not have permission to design beyond setback limits for your account.</p>
                        <br/>
                        <p><b>Note:</b> If this is a detached structure you can mark it as detached and then try adding modules</p>
                    </>,
                    messageType: AlertType.warning,
                    isModal: false,
                    isCloseAfterDisplay: true,
                })
            }


            SetVirMeshPos(this, e);
            SetCloneVirObj(this);
            return;
        }
        if (this.dragObst) {
            this.controls.enabled = true;
            PutDragObst(this);
            return;
        }
        if (this.selectType === 'module') {
            if (this.moveMode) {
                const committed = SetModuleOriPos(this, this.moduleMeshArr);
                if (committed) {
                    this.setMoveMode(false);
                } else if (this.mouseModuleStatus === 'down') {
                    this.setMoveMode(false);
                }
            } else {
                if (this.mouseModuleStatus === 'down') {
                    const interInfo = GetClickObj(e, this.moduleMeshArr.filter(module => {
                        return module.pathwayOver !== true
                    }), this.camera, this.cSize);
                    if (interInfo) {
                        SetSelModuleArr(this.moduleMeshArr, interInfo.object, e);
                    }
                }
            }
            this.posModuleDown = null;
        } else if (this.selectType === 'mapCustom') {
            this.posMapDown = null;
            this.posPlaneDown = null;
        }
        if (this.moveMode) return;
        if (!this.selectType || dragDis > 10) return;
        var selTypeMeshArr = this.meshArr.filter(mesh => {
            return mesh.visible && mesh.meshType === this.selectType
        });
        if (this.tabInfo === 'Obstruction Properties') {
            selTypeMeshArr = selTypeMeshArr;
        } else if (this.selectType === 'surface') {
            if (this.createPlaneMode) selTypeMeshArr = [this.planeMesh];
            else selTypeMeshArr = this.meshArr.filter(mesh => {
                return mesh.flatRoof === true
            });
        }
        this.selTypeMeshArr = selTypeMeshArr;
        const interObj: any = GetClickObj(e, this.selTypeMeshArr, this.camera, this.cSize);
        const newObstId = interObj ? interObj.object.obstId : undefined;

        // Select Roof/ Module and align camera to selected roof
        if (!isTapandHold && (e instanceof TouchEvent || (e instanceof MouseEvent && e.button !== 2)) && (this.selectType === 'roof' || this.selectType === 'module')) {
            const clickObj: any = GetClickObj(e, this.roofMeshArr, this.camera, this.cSize)
            const selRoofId = clickObj?.object?.roofFaceId;
            if (selRoofId) this.alignToRoof(selRoofId);
        }

        // Setup selection groups on click- Roof, Tree, Obstruction $ Surface mount
        if (this.selectType === 'roof') {
            SetWallMeshArr(this, interObj);
        } else if (this.selectType === 'tree') {
            const isRightClick = (e instanceof MouseEvent && e.button === 2) || isTapandHold;
            SetupTreeGroup(this, newObstId, isRightClick);
        } else if (this.selectType === 'obst') {
             SetupObstUI(this, newObstId);
        } else if (this.selectType === 'surface') {
            if (this.createPlaneMode && interObj?.point) CreatePlaneRoof(this, interObj.point);
            else if (!this.createRoofMode) SetupFlatRoof(this, interObj);
        }

        CheckModulesOverlay(this);
    }

    selectRightClickRoof = () => {
        this.roofMeshArr.forEach(roofMesh => {
            roofMesh.selectRoofFace = false;
        });
    }

    setCompassDrag = (val: boolean) => {
        this.compassDrag = val;
        SetInitCamRotVal(this);
    }

    setAutoRotate = (val: boolean) => {
        this.autoRotate = val;
        SetInitCamRotVal(this);
        if (this.viewMode === '3d') this.controls.enableRotate = !val;
        this.controls.enablePan = !val;
    }

    setSelectType = (selectType: SelectType) => {
        this.selectType = selectType;
        this.setPushMode(false);
        this.setMoveMode(false);
        this.sunGroup.visible = false;
        SetTreeTransArr(this, true);
        this.setManualZone(false);
        if (selectType !== "module") ResetSelModuleArr(this.moduleMeshArr);
    }

    setManualZone = (val: boolean) => {
        useViewerStore.setState({ manualZone: val });
        if (val && this.viewMode === '3d') {
            SetCameraView(this, '2d');
        } else if (!val && this.viewMode === '2d') {
            SetCameraView(this, '3d', true);
        }
        InitLineGroup(this);
    }

    setPushMode = (pushMode: boolean, copyMode?: boolean) => {
        this.pushMode = pushMode;
        if (this.viewMode === '3d') this.controls.enableRotate = !pushMode;
        if (copyMode) SetCopyVirObj(this);
        if (pushMode || this.selectType !== 'obst') this.obstId = null;
        this.transform.detach();
        if (this.virObs) this.virObs.visible = false;
        if (this.virTree) this.virTree.visible = false;
        if (this.virModule) this.virModule.visible = false;
        if (this.treeTransMesh) this.treeTransMesh.visible = false;


        if (pushMode) {
            if (this.selectType === 'tree') {
                this.virTree!.visible = true;
                SetTreeTransArr(this, true);
            } else if (this.selectType === 'obst') {
                this.virObs!.visible = true;
                SetupObstUI(this);
            } else if (this.selectType === 'module') InitModulePush(this);
        }
        if (this.selectType === 'module') {
            const activeBallastRackDirection = useViewerStore.getState().activeBallastRackDirection;
            if (! this.virModule) return;
            if(activeBallastRackDirection === "horizontal" ) {
                SetModuleSize(this.virModule.children[0], useViewerStore.getState().activeModule, "land", true);
            }
            else {
                SetModuleSize(this.virModule.children[0], useViewerStore.getState().activeModule, "port", true);
            }
            useViewerStore.setState(() => ({modulePlacementStatus: ModulePlacementStatusOk}));
        }
        if (pushMode && this.viewMode === '2d') this.setCameraView('3d');
    }

    setMoveMode = (moveMode: boolean) => {
        this.moveMode = moveMode;
        if (moveMode) {
            if (this.selectType === 'roof' && this.mapCustom) {
                this.setCameraView('2d');
                this.transform.attach(this.mapCustom);
            } else if (this.selectType === 'tree' && this.treeTransMesh) {
                this.transform.attach(this.treeTransMesh);
            } else if (this.selectType === 'obst' && this.viewMode === '2d') {
                this.setCameraView('3d');
            }
        } else {
            if (this.selectType === 'module') {
                const isMoveModuleActive = useViewerStore.getState().isMoveModuleActive
                if (isMoveModuleActive) {
                    useViewerStore.setState({isMoveModuleActive: false,isBallastedRackTypeModuleActive: false});
                    ResetSelModuleArr(this.moduleMeshArr)
                }
            }
            document.body.style.cursor = 'pointer';
            this.transform.detach();
            if (this.viewMode === '2d') this.setCameraView('3d');
        }
        if (this.selectType === 'module' || this.selectType === 'mapCustom' || this.selectType === 'roof') {
            this.controls.enableRotate = !moveMode
        }
    }

    setAutoAlignMode = (autoAlignMode: boolean) => {
        this.autoAlignMode = autoAlignMode;
        if (autoAlignMode) {
            this.autoScaleParams.current = 'map';
            this.autoScaleParams.mapRef = {};
            this.autoScaleParams.modelRef = {};
            InitLineGroup(this);
        } else {
            this.drawMode = false;
            // ResetLineMesh(this, 'stop');
            // resetAutoScale(this);
        }

        // else ResetLineMesh(this, 'pause');
        // if (this.selectType === 'mapCustom'){
        //     this.controls.enableRotate = !autoAlignMode
        // }
    }

    setDrawMode = (drawMode: boolean) => {
        this.drawMode = drawMode;
        if (!this.drawMode) ResetLineMesh(this, 'pause');
        const labelDrawStop = document.getElementById('labelDrawStop');
        if (!labelDrawStop) return;
        if (drawMode) labelDrawStop.classList.add('active');
        else labelDrawStop.classList.remove('active');
    }

    resetDraw = () => {
        this.setDrawMode(false);
        ResetLineMesh(this, 'stop');
    }

    setMeasureMode = (measureMode: boolean) => {
        this.measureMode = measureMode;
        InitLineGroup(this);
    }


    // MODULE CONTROLS ===============================

    setRoofModuleDir = (dir: ModuleOrientation) => {
        if (this.roofModuleTime) return;
        this.roofModuleTime = true;
        setTimeout(() => {
            this.roofModuleTime = false;
        }, 100);
        //switch to 3D mode if not already
        if (this.viewMode === '2d') {
            SetCameraView(this, '3d');
        }

        let selectedRoof : RoofMesh | undefined = undefined;
        for (let i = 0; i < this.roofMeshArr.length; i++) {
            if (this.roofMeshArr[i].selectRoofFace !== false) {
                selectedRoof = this.roofMeshArr[i];
                break;
            }
        }

        let roofMesh = selectedRoof ?? this.roofMeshArr.find(roof => roof.roofFaceId === this.preSelRoofId);

        if(roofMesh===null || roofMesh===undefined ){
            roofMesh = useViewerStore.getState().selRoofFaceArr[0]
        }

        addModulesToRoofAutoFill(this, roofMesh, null, dir, true);

        this.deselectAllSelectedObjectsAndEndTask()
        setPathname("/configure/modules");

    }

    spinAllModules = () => {
        const selRoofFaceArr = useViewerStore.getState().selRoofFaceArr;
        if (selRoofFaceArr.length == 0) {
            this.viewerAlert({
                show: true,
                title: "Alert : Spin All Modules Action",
                message: <SpinAllModules/>,
                messageType: AlertType.warning,
                isModal: true
            });
        } else {
            //if contains ballasted rack ,show cannot spin modules alert
            const modulesOnRoof = selRoofFaceArr.flatMap(roofFace => roofFace.children.filter(module => module.meshType === 'module'));
            const containsBallastedRack = modulesOnRoof.some(module => module.userData.isBallastedRackType === true);
            if (containsBallastedRack) {
                this.viewerAlert({
                    show: true,
                    title: "Cannot Spin Modules for Selected Roof",
                    message: "Cannot spin modules on a roof with Ballasted Racks.",
                    messageType: AlertType.warning,
                    isModal: false
                });
                return;
            }
            this.setSpinAllModulesByRoofFaces(selRoofFaceArr);
        }
    }

    setSpinAllModules = () => {
        this.moduleDir = FlipDir(this.moduleDir);
        // SetModuleArr(this);
        addModulesToAllRoofsMaxFill(this,null, this.moduleDir);
    }

    setSpinAllModulesByRoofFaces = (roofFaces: RoofMesh[]) => {
        const globalFlippedDir = FlipDir(this.moduleDir);

        roofFaces.forEach((roofFace) => {
            const flippedDir = roofFace.moduleArr.length > 0
                ? FlipDir(roofFace.moduleArr[0].dir)
                : globalFlippedDir;

            // SetModuleArr(this, "autoFill", roofFace.roofFaceId, flippedDir);
            addModulesToRoofAutoFill(this, roofFace,null, flippedDir);
        });

        this.moduleDir = globalFlippedDir;
    }

    setModuleSetting = (type: 'dir' | 'delete', value?: string) => {
        switch (type) {
            case 'delete': {
                const selModules = GetSelModules(this.moduleMeshArr);
                if (!selModules || !selModules.length) return;

                const selRoof = this.roofMeshArr.find(
                    item => item.roofFaceId === selModules[0].roofId
                );

                DeleteModule(selModules, selRoof, this.moduleMeshArr, this);
                return; // ✅ exit immediately, nothing else to do
            }

            case 'dir': {
                this.moduleDir = value ?? 'port';

                if (this.pushMode) {
                    SetVirModuleSize(this.virModule, type, value);
                    return;
                }

                const selModules = GetSelModules(this.moduleMeshArr);
                if (!selModules || !selModules.length) return;

                if (this.changeModuleTime) return;
                this.changeModuleTime = true;

                //if multiple ballasted racks are selected, show select only one module alert
                const selectedBallastedRacks = selModules.filter(module => module.userData.isBallastedRackType === true);
                if (selectedBallastedRacks.length > 1) {
                    this.viewerAlert({
                        show: true,
                        title: "Cannot Spin Multiple Ballasted Rack Modules",
                        message: "Please select only one Ballasted Rack module to change orientation.",
                        messageType: AlertType.warning,
                        isModal: false,
                        isCloseAfterDisplay : true,
                    });

                    this.changeModuleTime = false;
                    return;

                }

                UpdateSelModuleArr(this, 'dir', value);
                siteCalculationState.needsUpdate(CalculationType.MODULE_GRAPH);

                setTimeout(() => {

                    this.changeModuleTime = false;
                }, timeModuleDelay);


                return;
            }
        }
    };

    // SUN CONTROLS
    updateSunPosTime(time: number) {
        this.sunInfo.time = time;
        SetSunGroupPos(this.sunGroup, this.sunInfo);
    }

    updateSunPathDate(date: number) {
        const dateNew = new Date(dateFirst);
        dateNew.setDate(date);
        this.sunInfo.date = GetDateVal(dateNew);
        SetSunGroupPos(this.sunGroup, this.sunInfo);
    }

    showSunPathLine = (value: boolean) => {
        const sunPath = this.sunGroup.children[1];
        if (sunPath) sunPath.visible = value;
    }


    clearScene = () => {
        useViewerStore.setState({ customImagery: null });
        SetTreeTransArr(this, true);
        if (this.treeTransMesh) this.treeTransMesh.visible = false;
        this.transform.detach();

        this.roofJson = null;
        [this.lineGroup, this.boundGroup, this.roofGroup, this.treeGroup, this.obstGroup].forEach(group => {
            EmptyGroup(group);
        });
    }

    addToCart = () => {
        if (!this.selTreeArr || this.selTreeArr.length === 0) return;
        this.viewerAlert({
            show: true,
            title: "Fill Tree Info",
            message: <TreeCartForm
                selectedTrees={this.selTreeArr}
                onSubmit={() => this.setTreeProperty(TreeProperty.delete)}
            />,
            messageType: AlertType.info,
            isModal: true,
        })
    }

    setWallSize = (roofHeightValue: number) => {
        const nonFlatRoofMeshArr = this.roofMeshArr.filter(roof => !roof.flatRoof);

        for (const roofMesh of nonFlatRoofMeshArr) {
            const group: any = roofMesh.parent.parent;
            group.position.set(group.position.x, roofHeightValue + roofMesh.userData.initialHeight, group.position.z);
        }

        const wallMeshArr = this.meshArr.filter(m => m.name.startsWith('wallMesh'));
        const nonFlatWalls = wallMeshArr.filter(wall => !wall.userData.isGroundMount)

        for (const wall of nonFlatWalls) {
            const points = wall.userData.positions;
            const wallShape = new THREE.Shape();
            wallShape.moveTo(points[0].x, points[0].z);
            for (let i = 1; i < points.length - 1; i++) {
                wallShape.lineTo(points[i].x - roofHeightValue, points[i].z);
            }
            wallShape.lineTo(points[points.length - 1].x, points[points.length - 1].z);

            const extrudeSettings = {
                depth: wall.userData.depth ? wall.userData.depth - 0.1 : 0.04,
                bevelEnabled: false,
            };
            wall.geometry = new THREE.ExtrudeGeometry(wallShape, extrudeSettings);
            SetWallMeshOBB(wall, wall.userData.center, wall.userData.firstPos);
        }

        for (const edge of this.edgeMeshArr) {
            const isGMEdge = this.roofs[0].faces.find( (face : any) => face.roofFaceId === edge.userData.roofFaceId)?.surfaceMount;
            if (isGMEdge) continue;
            edge.position.y = roofHeightValue+0.01;
        }


        for (const obst of this.obstGroup.children) {

            const isDefaultGroundObstruction = obst.children[0].children[0].children[0].worldPos.y < 1

            if ((obst).isObtsructionOnGround || isDefaultGroundObstruction) continue
            obst.position.y += 50


            // Delete orphan obstruction
            if(!obst.roofId){
                console.log(obst)
                DeleteObst(obst, this)
            }

            //cast a ray downwards to get intersection point with the roof and then move the obstruction to that point
            const raycaster = new THREE.Raycaster();
            raycaster.set(obst.position, new THREE.Vector3(0, -1, 0));


            //DEBUG : Obstruction Positioning Arrow Helper
            // const arrowHelper = new THREE.ArrowHelper(raycaster.ray.direction, raycaster.ray.origin, 200, 0xff0000);
            // this.scene.add(arrowHelper);

            const intersects = raycaster.intersectObjects(this.roofMeshArr, false);

            if (intersects.length > 0) {
                const interPoint = intersects[0].point;
                obst.position.y = interPoint.y;
            }

        }

    };

    // CHANGE OBSTACLE PROPERTIES =================
    setTreeProperty = (type: TreeProperty, value?: number | string) => SetTreeProperty(this, type, value);
    selectAllTrees = () => SelectAllTrees(this);
    removeTreeFromCart = () => RemoveTreeFromCart(this.selTreeArr);
    setObstAngle = () => this.setObstProperty(ObstProperty.angle);
    setObstHeight = (value: number) => this.setObstProperty(ObstProperty.height, value);
    setObstLength = (value: number) => this.setObstProperty(ObstProperty.length, value);
    setObstWidth = (value: number) => this.setObstProperty(ObstProperty.width, value);
    setObstSides = (value: number) => this.setObstProperty(ObstProperty.sides, value);
    setObstDiameter = (value: number) => this.setObstProperty(ObstProperty.diameter, value);
    setObstRadius = (value: number) => this.setObstProperty(ObstProperty.radius, value);
    setObstShape = (value: number) => this.setObstProperty(ObstProperty.shape, value);
    setObstDelete = () => this.setObstProperty(ObstProperty.delete);
    setObstProperty = (type: ObstProperty, value?: number) => SetObstProperty(this, type, value);

    // TOGGLE LAYERS VISIBILITY ===================
    enableAllLayers = (isModulesTab?: boolean) => {
        resetLayers();
        Object.entries(useViewerStore.getState().layerVisibilityState).forEach(([layer, visible]) => {
            if (layer === 'tree') {
                const showTree = !isModulesTab;
                this.setVisibleGroup(layer, showTree);
                toggleLayer(layer, showTree);
            } else if (!visible) {
                this.setVisibleGroup(layer as LayerOption, true);
            }
        });
    }

    setVisibleGroup = (type: LayerOption, value: boolean) => SetVisibleGroup(this, type, value);
    hideObject = (type: HideObjectType, value: boolean) => {
        if (type === 'SunVector') {
            this.setSunDefault();
            this.sunGroup.visible = value;
        } else HideObject(this, type, value);
    }

    // CHANGE MESH MATERIAL COLOR/TEXTURE ==========
    setMeshMaterialColor = (meshArray: any[], color: string) => ChangeMeshMaterial(meshArray, color, 'color', this);
    setMeshTexture(meshArray: any[], roofShingleImg: any, type: 'map' | 'color', textureId: string) {
        ChangeMeshMaterial(meshArray, roofShingleImg, 'map', this, type, textureId);
    }
    resetRoofToDefault = () => this.setMeshMaterialColor(this.roofMeshArr, defaults.ROOF_FACE_COLOR);
    resetWallToDefault = () => this.setMeshMaterialColor(this.wallMeshArr, defaults.WALL_COLOR);
    resetObstToDefault = () => this.setMeshMaterialColor(this.obstMeshArr, defaults.OBSTRUCTION_COLOR);
    resetObstructionToDefault = () => this.setMeshMaterialColor(this.wallMeshArr, defaults.WALL_COLOR);
    updateCustomImageProperty = (type: CustomImageProperty, value: number) => SetMapCustomSize(this, type, value);


    // SHADE CALCULATION ===============
    refreshShade = () => RefreshShade();
    reCalcShade = (obstIds: string[], delStatus: boolean) => ReCalcShade(this, obstIds, delStatus);
    // setEPCStatus = () => SetDisplayShadePoints(this, 'epc'); // unused
    selectShadePoint = (roofFaceId: string, shadePtIdx: number) => {
        this.resetSelectedShade();

        const roofMesh = this.roofMeshArr.find(r => r.roofFaceId === roofFaceId);
        if (!roofMesh) return;

        const instancedShade = roofMesh.children.find( (c : any) => c.name === 'shadePoint') as THREE.InstancedMesh | undefined;
        if (!instancedShade) return;

        const shadeArr = roofMesh.userData.shadeArrForRoof;

        //  Set the selected shade point color to red
        const mat = instancedShade.material as THREE.Material & { vertexColors?: boolean };
        if ('vertexColors' in mat) mat.vertexColors = true;

        // Reset all to base color FIRST
        const tmpColor = new THREE.Color();
        for (let i = 0; i < shadeArr.length; i++) {
            instancedShade.setColorAt(i, tmpColor.setHex(0x0000ff)); // blue
        }

        // Now highlight the selected one
        const selectedColor = new THREE.Color(0xff0000); // red
        instancedShade.setColorAt(shadePtIdx, selectedColor);
        instancedShade.instanceColor!.needsUpdate = true;

        // (Re)set matrices (optional if positions may have changed)
        const dummy = new THREE.Object3D();
        shadeArr.forEach((shade: any, idx: number) => {
            dummy.position.set(shade.position.x, shade.position.y, 0);
            dummy.scale.set(1, 1, 1);
            dummy.updateMatrix();
            instancedShade.setMatrixAt(idx, dummy.matrix);
        });
        instancedShade.instanceMatrix.needsUpdate = true;

        this.alignToRoof(roofFaceId);
    };

    resetSelectedShade = () => {
        const base = new THREE.Color(0x0000ff);
        this.roofMeshArr.forEach(roofMesh => {
            const instancedShade = roofMesh.children.find( (c:any) => c.name === 'shadePoint') as THREE.InstancedMesh | undefined;
            if (instancedShade) {
                for (let i = 0; i < instancedShade.count; i++) {
                    instancedShade.setColorAt(i, base);
                }
                instancedShade.instanceColor!.needsUpdate = true;
            }
        });
    };


    // EDIT CONTROLS - Undo/ Redo ==========
    addStep = (key: EditAction, object: any, older?: any, newer?: any) => AddStep(this, key, object, older, newer);
    setStep = (stepDir: 1 | -1) => SetStep(this, stepDir);
    undo = () => this.setStep(-1);
    redo = () => this.setStep(1);

    setSunDefault = () => {
        this.sunInfo.date = dateToday;
        this.sunInfo.time = dateToday.getHours() + dateToday.getMinutes() / 60;
        this.updateSunPathDate((dateToday.getTime() - dateFirst.getTime()) / (24 * 3600 * 1000));
    }

    // IMPORT & EXPORT ==============

    downloadGLTF = async () => await DownloadGLTF(this);

    exportJson = async () => {
        const file: any = await ExportGen(this);
        if (!file) return;
        const link = document.createElement("a");
        link.href = URL.createObjectURL(file);
        const sfidString = sessionStorage.getItem('SalesForceId')
        link.download = `${sfidString}.genesis`;
        link.click();
        URL.revokeObjectURL(link.href);
        return true;
    }

    importGLBFile = (file: any) => {
        const newZip = new JSZip();
        var flagGltf = false;
        newZip.loadAsync(file).then((zip) => {
            for (let [filename, gltfFile] of Object.entries(zip.files)) {
                if (filename.split('.').pop() === 'gltf') {
                    flagGltf = true;
                    newZip.file(filename)?.async("string").then(content => {
                        const jsonInfo = JSON.parse(content);
                        if (jsonInfo.scenes && jsonInfo.scenes[0]) {
                            const { extras } = jsonInfo.scenes[0];
                            this.clearScene();
                            setTimeout(() => {
                                LoadModelJson(this, JSON.stringify(extras), 'glb');
                            }, 0);
                        }
                    });
                } else {
                    window.alert('Can not load the gltf file');
                    return;
                }
            }
            if (!flagGltf) {
                window.alert('Can not find gltf file in zip');

            }
        });
    }


    resizeCanvas = () => {

        const isStreetViewVisible = useViewerStore.getState().isStreetViewVisible

        const canvasContainer = document.getElementById('canvasContainer') as HTMLDivElement;

        if(!canvasContainer) return;

        if (isStreetViewVisible) {
            canvasContainer.classList.add('canvas-street-view-split')
        }

        const { clientWidth, clientHeight } = canvasContainer;

        this.cSize.w = isStreetViewVisible ? clientWidth / 2 : clientWidth;
        this.cSize.h = clientHeight;

        const updatedWidth = isStreetViewVisible ? window.innerWidth / 2 : clientWidth

        const aspect = updatedWidth / clientHeight;
        var camR = (this.camera.top * 2) / updatedWidth;
        const newWidth = updatedWidth * camR;

        // this.camera.aspect = aspect; // OrthographicCamera does not have 'aspect'
        this.camera.left = newWidth * aspect / -2;
        this.camera.right = newWidth * aspect / 2;
        this.camera.top = newWidth / 2;
        this.camera.bottom = newWidth / -2;
        if (this.resizeTimeout) clearTimeout(this.resizeTimeout);
        this.resizeTimeout = setTimeout(() => {
            this.camera.updateProjectionMatrix();
            this.renderer.setSize(updatedWidth, clientHeight);
            this.cssLabelrenderer.setSize(updatedWidth, clientHeight);
        }, 100);

    }

    setTabInfo = (value: string) => {
        this.tabInfo = value;
        // ResetWallMeshArr(this);
        // ShowSelectRoof(this);
        this.obstId = null;
        SetupObstUI(this);
    }

    deleteWall = (wallArr: any[]) => {
        wallArr.forEach(wall => {
            if (wall.select) wall.parent.visible = false;
        });
    }

    // Not used anymore
    // loadTexturedFBXMesh = (modelFile: any) => {
    //     let object = new FBXLoader().parse(modelFile);
    //     object.rotation.x = -Math.PI / 2;
    //     object.scale.set(0.35, 0.35, 0.35)

    //     const box3 = new THREE.Box3();
    //     box3.expandByObject(object, true)
    //     const vec = new THREE.Vector3();
    //     box3.getCenter(vec)
    //     vec.negate()

    //     //subtract
    //     vec.sub(new THREE.Vector3(-6, -7, 3.5))

    //     object.position.set(vec.x, vec.y, vec.z)

    //     this.ambientLight.intensity = 0.7

    //     this.scene.add(object);
    // }

    fetchMapImageAndLoadOnPlane = (vendor: ImageryOptions) => {
        if (vendor === ImageryOptions.CUSTOM) {
            fetchCustomMap(this);
        } else {
            FetchMapImage(this, vendor);
        }
    }

    setOrbitControlsMode = (mode: EditorControlMode) => {
        if (mode === 'orbit') {
            //make left click rotate
            this.controls.mouseButtons = { LEFT: THREE.MOUSE.ROTATE, MIDDLE: THREE.MOUSE.PAN };
            this.controls.update();
            useViewerStore.setState({ editorControlMode: EditorControlMode.orbit });
        } else if (mode === 'pan') {
            //make left click pan
            this.controls.mouseButtons = { LEFT: THREE.MOUSE.PAN, MIDDLE: THREE.MOUSE.PAN };
            this.controls.update();
            useViewerStore.setState({ editorControlMode: EditorControlMode.pan });
        }
    }

    setCreateRoofMode = (mode: boolean) => {
        this.createRoofMode = mode;

        if (mode) {
            this.setCreatePlaneMode(false);
            if (this.viewMode === '3d') SetCameraView(this, '2d');
        } else {
            if (this.viewMode === '2d') SetCameraView(this, '3d');
        }
        InitLineGroup(this);
        const btnCreateRoofMode = document.getElementsByClassName('btnCreateRoofMode')[0];
        if (btnCreateRoofMode) btnCreateRoofMode.textContent = mode ? 'Stop Draw' : 'Draw Roof';
    }

    setCreatePlaneMode = (mode: boolean) => {
        this.createPlaneMode = mode;
        if (mode) this.setCreateRoofMode(false);
        const btnCreatePlaneMode = document.getElementsByClassName('btnCreatePlaneMode')[0];
        if (btnCreatePlaneMode) btnCreatePlaneMode.textContent = mode ? 'Stop' : 'Add';
    }

    alertSelectedLatLongPoints = (e: any) => {
        const interPlane = GetClickObj(e, [this.planeMesh], this.camera, this.cSize);
        if (this.mapCoordinates && !this.isViewerInSplitMode) {
            ViewAlertLatLon(this, interPlane.point);
        }
    }

    deselectAllSelectedObjectsAndEndTask = () => {

        if (this.downPos) return
        this.selFlatRoof = null;

        //Reset selectType
        const actualSelectType = this.getSelectTypeFromPath(getPathname())
        this.setSelectType(actualSelectType)

        if (useViewerStore.getState().isPushPullMode) {
            clickButtonWithRef("pushPullToolBtn")
        }
        //Deselect Ballasted Racking
        //UPDATE UI
        useViewerStore.setState({
            isMoveRoofActive: false,
            isAddModuleActive: false,
            isMoveModuleActive: false,
            isAddTreeActive: false,
            isMoveTreeActive: false,
            isAddObstructionActive: false,
            isMoveObstructionActive: false,
            isBallastedRackTypeModuleActive: false
        })

        document.body.style.cursor = "default";

        //Remove Custom Context Menu
        useViewerStore.setState({ pointerDownSelectedObjectType: null })


        if (getPathnameIncludes('obstruction')) {
            // document.getElementById("editObstBtn").click(); //This button is not there in the DOM
        } else if (getPathnameIncludes('modules')) {
            if (this.virModule) this.virModule.visible = false
            this.selectType = SelectType.module;
            this.setPushMode(false);
            this.setMoveMode(false);
        }

        this.removeSelectionBoxHelper();

        //End Push-Mode ( add )
        if (this.pushMode) {
            this.setPushMode(false)
        }

        //End Move-Mode ( move objects )
        if (this.moveMode) {
            this.setMoveMode(false)
        }

        //Deselect all roof faces
        this.roofMeshArr.forEach((mesh) => {
            const { oriMap, oriColor, roofFaceId } = mesh;
            mesh.selectRoofFace = false
            mesh.material.color.setHex(oriColor);
            mesh.material.map = oriMap;
            const selectRoofInfo = document.getElementById('selectRoofInfo');
            const wholeRoofInfo = document.getElementById('wholeRoofInfo');
            if (selectRoofInfo) selectRoofInfo.style.display = 'none'
            if (wholeRoofInfo) wholeRoofInfo.style.display = 'block'
        })

        //Deselect Modules
        ResetSelModuleArr(this.moduleMeshArr)

        //Deselect Tree
        this.treeGroup.children.forEach((tree: any) => {
            tree.select = false;
            removeSelectedTree(tree);
            tree.children[0].material.emissive.setHex(0x000000);
        });

        //Deselect Obstructions
        SetupTreeGroup(this, '');
        SetupObstUI(this);

        //Deselect Walls
        SetWallMeshArr(this, null);


    }


    getSelectTypeFromPath = (pathname: string): SelectType => {
        let selectType = SelectType.roof; //default select type is roof
        if (pathname.includes('custom-image')) {
            selectType = SelectType.mapCustom;
        } else if (pathname.includes('obstruction')) {
            selectType = SelectType.obst;
        } else if (pathname.includes('tree')) {
            selectType = SelectType.tree;
        } else if (pathname.includes('roof')) {
            selectType = SelectType.roof;
        } else if (pathname.includes('modules')) {
            selectType = SelectType.module;
        } else if (pathname.includes('surface')) {
            selectType = SelectType.surface;
        } else if (pathname.includes('what-if')) {
            selectType = SelectType.whatIf;
        } else if (pathname.includes('price-calculation')) {
            selectType = SelectType.epc;
        } else if (pathname.includes('irradiance') || pathname.includes('solar-access')) {
            if (pathname.includes('irradiance')) selectType = SelectType.sun;
        }
        return selectType;
    }


    // TREE PERIMETER TOOL

    showRoofSquirrel = (value: boolean) => {
        //home position
        // document.getElementById('defaultPositionBtn').click()
        const circle = this.totalGroup.getObjectByName('roofSquirrelCircle');
        if (circle) circle.visible = value;
    }

    updateRoofSquirrelRadius = (radius: number) => {
        const circleMesh: any = this.totalGroup.getObjectByName('roofSquirrelCircle')
        if (!circleMesh) return console.log('Roof Squirrel Circle Mesh not found');
        const circle = new THREE.CircleGeometry(radius, circleMesh.geometry.parameters.segments);
        circleMesh.geometry.dispose()
        circleMesh.geometry = circle
    }

    calculateRoofSquirrel = () => {
        const treeInCircle = []
        const circle: any = this.totalGroup.getObjectByName('roofSquirrelCircle')
        if (!circle) return console.log('Roof Squirrel Circle Mesh not found');
        const radius = circle.geometry.parameters.radius

        this.treeGroup.children.forEach((mesh) => {
            //check if tree collides with circle
            const treePos = mesh.position.clone()
            treePos.y = 0
            const distance = treePos.distanceTo(circle.position)
            if (distance < radius) {
                treeInCircle.push(mesh)
            }
        })

        const radiusInFeet = Math.round(radius * 3.28084)

        //show modal
        this.viewerAlert({
            show: true,
            title: "Tree Perimeter Tool Info",
            message: `There are ${treeInCircle.length} trees in the ${radiusInFeet} feet radius of the roof`,
            messageType: AlertType.info,
            isModal: false
        })
    }

    checkIfRoofIsSmallFace = (roofMesh: RoofMesh) => {
        //If roof_size_type is small_face then don't show the roof tag (Area < 5200 sq_inch)
        const roof_size_type = roofMesh.userData.roof_size_type
        return roof_size_type === 'small_face'
    }


    highlightRoof = (roofId: string, value: boolean) => {
        this.roofMeshArr.forEach(mesh => {
            const { oriMap, oriColor, userData } = mesh;
            if (userData.roof_tag_id === Number(roofId)) {
                if (value) {
                    mesh.material.map = undefined;
                    mesh.material.color.setHex(0x8AC7DB);
                } else {
                    mesh.material.color.setHex(oriColor);
                    mesh.material.map = oriMap;
                }
            }
        });
    }


    togglePathway = (pathwayId: string) => {
        const selPathway = useViewerStore.getState().pointerDownSelectedObject;
        if (!selPathway || !selPathway.pathwayId) return;
        SetTogglePathwayValue(this, pathwayId || selPathway.pathwayId);
    }

    // LABELS & TAGS
    updatePitchOverrideText = (roofTagId: number, value: number) => UpdatePitchOverrideText(this, roofTagId, value);
    toggleRoofTag = (show: boolean) => ToggleRoofTag(this, show);
    toggleRoofIDTriangle = (show: boolean) => ToggleRoofIDTriangle(this, show);
    reLabelTreeGroup = () => ReLabelTreeGroup(this);
    toggleTreeLabel = (forReport?: boolean, heightLabel?: boolean) => ToggleTreeLabel(this, forReport, heightLabel);
    toggleTreeMultiplierLabel = (show: boolean) => ToggleTreeMultiplierLabel(this, show);
    toggleModuleMultiplierLabel = (visible: boolean) => ToggleModuleMultiplierLabel(this, visible);
    clearTreeLabel = () => {
        this.treeTagGroup.visible = false;
        this.treeTagGroup.clear();
    }

    setQuestionable(roof: RoofMesh, noteId: number, otherReason?: string, onSubmit?: (success: boolean, selectedQnRoofId: number, otherReasonInput: string) => {}) {
        this.viewerAlert({
            show: true,
            title: "Set Questionable Roof",
            message: <QuestionableRoof
                roof={roof}
                noteId={noteId}
                otherReason={otherReason ?? roof.userData.questionableRoofNotes ?? ""}
                onSubmit={onSubmit} />,
            messageType: AlertType.info,
            isModal: true
        })
    }

    resetViewAfterReportGeneration = (reportType: ReportType) => {
        if (reportType === ReportType.direct) {
            this.setCameraView('3d', true)
            this.setTopView(false)
            this.setVisibleGroup("tree", true)
            this.setVisibleGroup("pathway", true)
            this.setVisibleGroup("setback", true)
            this.toggleRoofIDTriangle(false);
            useViewerStore.setState({
                moduleSolarAccess: true
            })
            this.hideObject(HideObjectType.ModuleTexture, false)

        } else if (reportType === ReportType.measure) {
            this.moduleMeshArr.forEach((mesh) => {
                mesh.material.map = undefined;
                mesh.material.needsUpdate = true
            })
            this.setCameraView('3d', true)
            this.renderer.setClearColor(0x000000, 0);
            this.setVisibleGroup("tree", true)
            this.setVisibleGroup("maps", true)
            this.setVisibleGroup("pathway", true)
            this.setVisibleGroup("setback", true)
            if (this.mapCustom) this.mapCustom.visible = true
            this.toggleRoofIDTriangle(false)
            this.hideObject(HideObjectType.ModuleTexture, false)

        } else if (reportType === ReportType.site) {
            this.setTopView(false)
            this.setVisibleGroup("tree", true)
            this.hideObject(HideObjectType.GhostTree, useViewerStore.getState().isHideAllGhostTrees);
            this.setVisibleGroup("setback", true)
            this.setVisibleGroup("pathway", true)
            this.setCameraView('3d', true)
            this.clearTreeLabel()
            this.showRoofSquirrel(false)
            this.renderer.setClearColor(0x000000, 0);
        }

    }

    shiftUpBlackBorders = (arr: any[]) => {
        for (let i = 0; i < arr.length; i++) {
            arr[i].position.y += this.shiftBlackBordersValue;
        }
    }

    shiftDownBlackBorders = (arr: any[]) => {
        for (let i = 0; i < arr.length; i++) {
            arr[i].position.y -= this.shiftBlackBordersValue;
        }
    }

    // GENERATE SITE IMAGES ===================

    getImageForDirectLayout = async () => {
        this.shiftUpBlackBorders(this.edgeMeshArr);

        return new Promise((resolve, reject) => {
            useViewerStore.setState({
                pathname: '/configure/modules',
                moduleSolarAccess: false
            })
            this.hideObject(HideObjectType.ModuleTexture, true)
            this.setVisibleGroup("tree", false)
            this.setVisibleGroup("pathway", false)
            this.setVisibleGroup("setback", false)
            this.toggleRoofIDTriangle(true);
            this.moveCameraToSiteMidpointView();

            setTimeout(() => {
                resolve(this.getSquareImageOfSite())
                this.resetViewAfterReportGeneration(ReportType.direct);
                this.shiftDownBlackBorders(this.edgeMeshArr);
            }, 2000);
        });

    }

    getImageForMeasureLayout = async () => {
        return new Promise((resolve, reject) => {
            this.shiftUpBlackBorders(this.edgeMeshArr);
            this.setTopView(true)
            this.setVisibleGroup("tree", false)
            this.setVisibleGroup("maps", false)
            this.mapCustom!.visible = false
            this.renderer.setClearColor(0xffffff);
            this.setVisibleGroup("pathway", false)
            this.setVisibleGroup("setback", false)
            this.toggleRoofIDTriangle(true)
            this.moveCameraToSiteMidpointView();

            const textureLoader = new TextureLoader()

            textureLoader.load(moduleTextureForMeasureLayout, (moduleTexture) => {

                this.moduleMeshArr.forEach((mesh) => {

                    const modTexture = moduleTexture.clone()

                    modTexture.rotation = Math.PI / 2


                    if (mesh.dir === 'land') {
                        modTexture.repeat.set(0.6, 0.6)
                    } else {
                        modTexture.repeat.set(1, 1)
                    }

                    mesh.material = new THREE.MeshBasicMaterial({
                        map: modTexture
                    })
                    mesh.material.needsUpdate = true

                })

                setTimeout(() => {
                    resolve(this.getSquareImageOfSite(true));
                    this.resetViewAfterReportGeneration(ReportType.measure);
                    this.shiftDownBlackBorders(this.edgeMeshArr);
                }, 2000);


            })
        });
    }

    getImagesForSiteReport = (type?: ScreenShotType) => {

        // --- helper: get site bounds ---
        const computeSiteBounds = () => {
            const boundingBox = new THREE.Box3().setFromObject(this.roofGroup);
            const size = new THREE.Vector3();
            boundingBox.getSize(size);
            const center = new THREE.Vector3();
            boundingBox.getCenter(center);
            return { boundingBox, size, center };
        };

        // --- helper: directional view positioning ---
        const prepareCameraForDirectionalViews = (x: number, y: number, z: number) => {
            const { size } = computeSiteBounds();

            // base zoom setup
            const baseZoom = 18.5;

            // dynamic adjustment based on site width (x,z size)
            const maxDimension = Math.max(size.x, size.z);

            // pick a reference scale — this number controls how quickly zoom changes
            const referenceSize = 75; // roughly fits 100m wide sites well
            const scaleFactor = referenceSize / maxDimension;

            // clamp zoom so we don’t get absurd values
            const dynamicZoom = Math.max(6, Math.min(baseZoom * scaleFactor, 20));

            this.camera.position.set(x, y, z);
            this.camera.zoom = dynamicZoom;
            this.camera.updateProjectionMatrix();
        }

        const getCanvasImage = () => {
            this.controls.update();  // sync controls
            this.renderer.render(this.scene, this.camera);
            return this.renderer.domElement.toDataURL('image/jpeg');
        };

        return new Promise(async (resolve, reject) => {
            this.shiftUpBlackBorders(this.edgeMeshArr);
            this.setPushMode(false);
            let imagesArr: { home?: any, north?: any, east?: any, west?: any, tree?: any } = {};
            const waitTime = 1000;

            useViewerStore.setState({
                moduleSolarAccess: true,
                pathname: '/configure/modules'
            })

            this.controls.enabled = false; // Disable user controls
            this.deselectAllSelectedObjectsAndEndTask();

            // GENERATE HOME POSITION VIEW
            if (!type || type === ScreenShotType.home) {
                this.setVisibleGroup("pathway", false);
                this.setVisibleGroup("setback", false);
                this.toggleRoofIDTriangle(true);
                this.setVisibleGroup("tree", false); // Hide trees for topview
                this.moveCameraToSiteMidpointView();
                await sleep(waitTime);
                imagesArr.home = this.getSquareImageOfSite()
            }

            const origControlTarget = {
                x: this.controls.target.x,
                y: this.controls.target.y,
                z: this.controls.target.x,
            };

            this.controls.target.set(origControlTarget.x, origControlTarget.y, origControlTarget.z);
            this.toggleRoofIDTriangle(false);
            this.setTopView(false);
            this.setCameraView('3d', true)
            this.setVisibleGroup("tree", true)
            this.hideObject(HideObjectType.GhostTree, useViewerStore.getState().isHideAllGhostTrees);

            // --- GENERATE NORTH VIEW ---
            if (!type || type === ScreenShotType.north) {
                prepareCameraForDirectionalViews(6.740412343680213e-9, 73.37435872236647, 88.88308884192448);
                imagesArr.north = await getCanvasImage();
            }

            // --- GENERATE EAST VIEW ---
            if (!type || type === ScreenShotType.east) {
                prepareCameraForDirectionalViews(69.15289852382053, 92.2043574397518, 0.4827990025955498);
                imagesArr.east = await getCanvasImage();
            }

            // --- GENERATE WEST VIEW ---
            if (!type || type === ScreenShotType.west) {
                prepareCameraForDirectionalViews(-84.9318898718916, 77.89357505292065, -1.7790582520239904);
                imagesArr.west = await getCanvasImage();
            }

            // --- GENERATE TREES VIEW ---
            if (!type || type === ScreenShotType.tree) {
                const { size } = computeSiteBounds();
                const maxDimension = Math.max(size.x, size.z);
                const referenceSize = 100;
                const scaleFactor = referenceSize / maxDimension;
                const dynamicZoom = Math.max(3.25, Math.min(5 * scaleFactor, 7)); // narrower range

                this.setCameraView('2d', true);
                this.moveCameraToSiteMidpointView();
                this.toggleTreeLabel(true, false);

                this.renderer.setClearColor(0xffffff); // white background
                this.camera.zoom = dynamicZoom;
                this.camera.updateProjectionMatrix();

                imagesArr.tree = await getCanvasImage();
            }

            this.controls.enabled = true; // Enable user controls back

            // RESET VIEWS
            setTimeout(() => {
                resolve(imagesArr)
                this.resetViewAfterReportGeneration(ReportType.site);
                this.shiftDownBlackBorders(this.edgeMeshArr);
            }, 2000);
        })
    }

    getSquareImageOfSite = (isMeasureLayout = false) => {
        // Compute bounding box
        const boundingBox = new THREE.Box3().setFromObject(this.roofGroup);
        const size = new THREE.Vector3();
        boundingBox.getSize(size);
        const center = new THREE.Vector3();
        boundingBox.getCenter(center);

        // Dynamic zoom logic
        const maxDimension = Math.max(size.x, size.z);
        const paddingFactor = 1.2; // add slight padding
        const halfViewSize = (maxDimension / 2) * paddingFactor;

        const near = 0.1;
        const far = size.y + 100;

        // Create a temporary orthographic camera
        const orthoCam = new THREE.OrthographicCamera(
            -halfViewSize, halfViewSize,
            halfViewSize, -halfViewSize,
            near, far
        );

        // Position above the site
        orthoCam.position.set(center.x, boundingBox.max.y + size.y + 10, center.z);
        orthoCam.lookAt(center.x, center.y, center.z);
        orthoCam.up.set(0, 0, -1);

        // Create a separate temporary renderer (do NOT reuse main renderer)
        const tempRenderer = new THREE.WebGLRenderer({
            antialias: true,
            alpha: true,
            preserveDrawingBuffer: true
        });
        tempRenderer.setSize(2000, 2000);
        if (isMeasureLayout) tempRenderer.setClearColor(0xffffff);
        else tempRenderer.setClearColor(0x000000, 0); // transparent background

        // Temporarily add camera (optional for bounding)
        this.scene.add(orthoCam);

        // Temporarily adjust light for this render only
        const oldLightIntensity = this.ambientLight.intensity;
        this.ambientLight.intensity = 1.0;

        // Render
        tempRenderer.render(this.scene, orthoCam);

        // Capture image
        const imgData = tempRenderer.domElement.toDataURL('image/jpeg');

        // Cleanup — restore everything
        this.ambientLight.intensity = oldLightIntensity;
        this.scene.remove(orthoCam);
        tempRenderer.dispose();

        return imgData;
    }

    updatePitchAdjustedByUserFlag = () => {

        //If Original site, reset all pitchOverride to oriAng.tilt
        if(isDesignOriginalSite()){
            this.roofs[0]?.faces?.map((face : FaceItem) => {
                face.pitchOverride = face.oriAng.tilt;
            });
            useViewerStore.setState({
                isPitchOverrideByUser: false
            });
            return
        }

        //Set pitchOverride value

        const designList = useViewerStore.getState().designList;

        const { pitchOverrideArray, roofPitchArr } = designList?.flatMap(design =>
            design.Buildings?.flatMap((building: BuildingDesignData) =>
                building.Roofs?.map(roof => {
                    //fix if all pitch override values are 0 , then it is not overridden by user
                    if(roof.Pitch_Override === 0){
                        roof.Pitch_Override = roof.Tilt;
                    }

                    const roofId = Number(roof.Designation.replace(/\D/g, ''));
                    return {
                        pitchOverride: {
                            roofId,
                            pitch: Math.round(roof.Pitch_Override) ?? Math.round(roof.Tilt)
                        },
                        roofPitch: {
                            roofId,
                            pitch: Math.round(roof.Tilt)
                        }
                    };
                })
            ) ?? []
        ).reduce(
            (acc, item) => {
                acc.pitchOverrideArray.push(item.pitchOverride);
                acc.roofPitchArr.push(item.roofPitch);
                return acc;
            },
            { pitchOverrideArray: [] as any[], roofPitchArr: [] as any[] }
        ) ?? { pitchOverrideArray: [], roofPitchArr: [] };


        //check if roofPitchArr ( original pitch ) and pitchOverrideArray ( override pitch ) are same
        const isPitchOverrideByUser = JSON.stringify(roofPitchArr) !== JSON.stringify(pitchOverrideArray);


        pitchOverrideArray?.forEach((roof : any) => {
            const roofFace = this?.roofs[0]?.faces?.find((face: RoofUserData) => face.roof_tag_id === roof.roofId);
            if (roofFace) {
                roofFace.pitchOverride = roof.pitch;
            }

        });

        useViewerStore.setState({
            roofPitchOverrideArr: pitchOverrideArray,
            isPitchOverrideByUser: isPitchOverrideByUser
        });

    };

    // Not used anymore
    // castRayFromGivenPointToImagery = (x,y,z) => {
    //     const raycaster = new THREE.Raycaster();
    //     const point = new THREE.Vector3(x, 1, z);

    //     raycaster.set(point, new THREE.Vector3(0, -1, 0));

    //     const arrowHelper = new THREE.ArrowHelper(raycaster.ray.direction, raycaster.ray.origin, 100, 0xff0000);
    //     this.totalGroup.add(arrowHelper);


    //     const intersects = raycaster.intersectObjects(this.planeMesh);
    //     if (intersects.length > 0) {
    //         return intersects[0].point;
    //     }
    // }

    ifContainsFlatRoofAndEnableBallastedRacking = () => {
        useViewerStore.setState({doesDesignContainFlatRoof: false});
        this.roofMeshArr.forEach((roof) => {
            if (roof.userData.pitchOverride < 3) {
                useViewerStore.setState({doesDesignContainFlatRoof: true});
            }
        });
    }

    //DEBUGGING PURPOSES
    makeAllRoofFacesTransparent = () => {
        this.roofMeshArr.forEach((mesh) => {
            //make it see through
            mesh.material.transparent = true;
            mesh.material.opacity = 0.4;
            mesh.material.needsUpdate = true;
        });
    }


}
